import React, { useEffect, useState, useMemo } from "react";
import { Radio, Input, Tag, Button, Table, Pagination, Tooltip } from "antd";
import type { ColumnsType } from "antd/lib/table";
import { InfoCircleOutlined } from "@ant-design/icons";
import { commonProject, commonStudy } from "components/Drawer/constants";
import { ENVIRONMENT } from "const";

console.log("Environment variable in SelectionPopup component:", ENVIRONMENT);
// Types
export interface ProjectItem {
  project_id: string;
  project_name: string;
}

export interface ClinicalStudyItem {
  study_id: string;
  study_name: string;
  project_id: string;
  project_name: string;
}

export type SelectionType = "project" | "study";

export type ItemType = ProjectItem | ClinicalStudyItem;

interface SelectionPopupProps {
  onSelect: (selectionType: SelectionType, items: string[]) => void;
  projects: ProjectItem[];
  clinicalStudy: ClinicalStudyItem[];
  selectedItemsProp: ItemType[];
  selectionTypeProp: SelectionType | null;
}

const SelectionPopup: React.FC<SelectionPopupProps> = ({ onSelect, projects, clinicalStudy, selectedItemsProp, selectionTypeProp }) => {
  const isQaOrProd = ENVIRONMENT === "qa" || ENVIRONMENT === "prod";
  const hideCommonItemsInList = isQaOrProd;
  // Compute defaults once per environment mode.
  const defaultProjectItems = useMemo(
    () => (isQaOrProd ? [] : [commonProject]),
    [isQaOrProd],
  );
  const defaultStudyItems = useMemo(
    () => (isQaOrProd ? [] : [commonStudy]),
    [isQaOrProd],
  );
  // In qa/prod common project/study are hidden and not auto-preselected, so
  // "selected by default" messaging must not appear.
  const areCommonItemsAutoPreselected = !isQaOrProd;
  const [selectionType, setSelectionType] = useState<SelectionType>(selectionTypeProp || "project");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProjectItems, setSelectedProjectItems] = useState<ProjectItem[]>(defaultProjectItems);
  const [selectedStudyItems, setSelectedStudyItems] = useState<ClinicalStudyItem[]>([commonStudy]);
  const [selectedItems, setSelectedItems] = useState<ItemType[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
    setSearchTerm("");
    if (selectionType === "project") {
      setSelectedItems(selectedProjectItems.length ? selectedProjectItems : defaultProjectItems);
    } else {
      setSelectedItems(selectedStudyItems.length ? selectedStudyItems : defaultStudyItems);
    }
  }, [selectionType, selectedProjectItems, selectedStudyItems, defaultProjectItems, defaultStudyItems]);
    
  // Initialize all selected states on mount or when selectedItemsProp/selectionTypeProp changes
  useEffect(() => {
    if (selectedItemsProp && selectedItemsProp.length) {
      if (selectionTypeProp === "project") {
        setSelectedProjectItems(selectedItemsProp as ProjectItem[]);
        setSelectedStudyItems(defaultStudyItems);
        setSelectedItems(selectedItemsProp as ProjectItem[]);
      } else if (selectionTypeProp === "study") {
        setSelectedStudyItems(selectedItemsProp as ClinicalStudyItem[]);
        setSelectedProjectItems(defaultProjectItems);
        setSelectedItems(selectedItemsProp as ClinicalStudyItem[]);
      }
    } else {
      setSelectedProjectItems(defaultProjectItems);
      setSelectedStudyItems([commonStudy]);
      setSelectedItems(selectionTypeProp === "study" ? [commonStudy] : defaultProjectItems);
    }
  }, [selectedItemsProp, selectionTypeProp, defaultProjectItems, defaultStudyItems]);

  const removeDuplicates = (data: ItemType[]) => {
    const uniqueIds = new Set();
    return data.filter(item => {
      const id = selectionType === "project" ? (item as ProjectItem).project_id : (item as ClinicalStudyItem).study_id;
      if (uniqueIds.has(id)) {
        return false; // Duplicate found
      }
      uniqueIds.add(id);
      return true; // Unique item
    });
  };

  const filteredData = useMemo(() => {
    const source = (selectionType === "project" ? projects : clinicalStudy).filter((item) => {
      // In QA/PROD, hide the common project and common study from the selectable list
      if (hideCommonItemsInList) {
        if (selectionType === "project") {
          return item.project_id !== commonProject.project_id;
        } else {
          return (item as ClinicalStudyItem).study_id !== commonStudy.study_id;
        }
      }
      return true;
    });
    return source.filter((item) => {

      const searchLower = searchTerm?.toLowerCase();

      if (selectionType === "project") {
        // Filter by project_name and project_id
        return (
          item.project_name?.toLowerCase().includes(searchLower) ||
          item.project_id?.toString().includes(searchLower) // Convert ID to string for comparison
        );
      } else {
        // Filter by study_id, study_name, and project_name
        return (
          (item as ClinicalStudyItem).study_name?.toLowerCase().includes(searchLower) ||
          (item as ClinicalStudyItem).study_id?.toString().includes(searchLower) || // Convert ID to string for comparison
          (item as ClinicalStudyItem).project_name?.toLowerCase().includes(searchLower)
        );
      }
    });
  }, [searchTerm, selectionType, projects, clinicalStudy, hideCommonItemsInList, commonProject.project_id, commonStudy.study_id]);

  const pageSize = 10;
  const pagedData = useMemo(() => {
    const uniqueData = removeDuplicates(filteredData);
    return uniqueData.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [filteredData, currentPage]);


  const handleSelect = (item: ItemType) => {
    const exists = selectedItems.some((i) =>
      selectionType === "project"
        ? (i as ProjectItem).project_id === (item as ProjectItem).project_id
        : (i as ClinicalStudyItem).study_id === (item as ClinicalStudyItem).study_id,
    );
    if (!exists) {
      const newItems = [...selectedItems, item];
      setSelectedItems(newItems);
      if (selectionType === "project") {
        setSelectedProjectItems(newItems as ProjectItem[]);
      } else {
        setSelectedStudyItems(newItems as ClinicalStudyItem[]);
      }
    }
  };

  const removeItem = (item: ItemType) => {
    setSelectedItems((prev) => {
      const filtered = prev.filter((i) =>
        selectionType === "project"
          ? (i as ProjectItem).project_id !== (item as ProjectItem).project_id
          : (i as ClinicalStudyItem).study_id !== (item as ClinicalStudyItem).study_id,
      );
      if (selectionType === "project") {
        setSelectedProjectItems(filtered as ProjectItem[]);
      } else {
        setSelectedStudyItems(filtered as ClinicalStudyItem[]);
      }
      return filtered;
    });
  };

  const applySelection = () => {
    const selectedIds =
      selectionType === "project"
        ? selectedItems.map((item) => (item as ProjectItem).project_id)
        : selectedItems.map((item) => (item as ClinicalStudyItem).study_id);

    onSelect(selectionType, selectedIds);
  };

  const renderActionButton = (record: ItemType) => {
    const isSelected = selectedItems.some((i) =>
      selectionType === "project"
        ? (i as ProjectItem).project_id === (record as ProjectItem).project_id
        : (i as ClinicalStudyItem).study_id === (record as ClinicalStudyItem).study_id,
    );

    return (
      <Button
        type={isSelected ? "default" : "primary"}
        onClick={() => !isSelected && handleSelect(record)}
        disabled={isSelected}
      >
        {isSelected ? "Selected" : "Select"}
      </Button>
    );
  };

  const columns: ColumnsType<ItemType> =
    selectionType === "project"
      ? [
        {
          title: "Project ID",
          dataIndex: "project_id",
          key: "project_id",
        },
        {
          title: "Project Name",
          dataIndex: "project_name",
          key: "project_name",
        },
        {
          key: "action",
          render: (_, record) => renderActionButton(record),
        },
      ]
      : [
        {
          title: "Study ID",
          dataIndex: "study_id",
          key: "study_id",
        },
        {
          title: "Study Name",
          dataIndex: "study_name",
          key: "study_name",
        },
        {
          title: "Project Name",
          dataIndex: "project_name",
          key: "project_name",
        },
        {
          key: "action",
          render: (_, record) => renderActionButton(record),
        },
      ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div style={{ paddingBottom: 8 }}>
        <Radio.Group
          value={selectionType}
          onChange={(e) => setSelectionType(e.target.value)}
        >
          <Radio value="project">R&D Project ID</Radio>
          <Radio value="study">Study ID</Radio>
        </Radio.Group>

        <Input.Search
          placeholder="Enter search text"
          onSearch={(val) => setSearchTerm(val)}
          enterButton
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="my-2"
        />

        <div>
          {selectedItems
            .filter((item) =>
              selectionType === "project"
                ? (item as ProjectItem).project_id !== undefined
                : (item as ClinicalStudyItem).study_id !== undefined,
            )
            .map((item) => {
              // Check if current item is the common project or study
              const isCommonItem = selectionType === "project"
                ? (item as ProjectItem).project_id === commonProject.project_id
                : (item as ClinicalStudyItem).study_id === commonStudy.study_id;

              // Only treat as "auto-preselected" in non-qa/prod environments.
              const isAutoPreselectedCommonItem = isCommonItem && areCommonItemsAutoPreselected;

              const tagElement = (
                <Tag
                  closable={selectedItems.length > 1}
                  key={
                    selectionType === "project"
                      ? `project-${(item as ProjectItem).project_id}`
                      : `study-${(item as ClinicalStudyItem).study_id}`
                  }
                  onClose={() => removeItem(item)}
                >
                  {selectionType === "project"
                    ? (item as ProjectItem).project_name
                    : `${(item as ClinicalStudyItem).study_name} - ${(item as ClinicalStudyItem).project_name}`}
                  {
                    // Only show info icon when the common item was auto-preselected
                    // and is the only selection (not in qa/prod where nothing is
                    // preselected and the user chose this item themselves)
                    isAutoPreselectedCommonItem && selectedItems.length === 1 && (
                      <InfoCircleOutlined style={{ marginLeft: 4 }} />
                    )
                  }
                </Tag>
              );

              // Show tooltip only when the common item was auto-preselected and
              // is the only selection — avoids misleading message in qa/prod
              if (isAutoPreselectedCommonItem && selectedItems.length === 1) {
                return (
                  <Tooltip
                    key={
                      selectionType === "project"
                        ? `tooltip-project-${(item as ProjectItem).project_id}`
                        : `tooltip-study-${(item as ClinicalStudyItem).study_id}`
                    }
                    title="Common Project/Study is selected by default. Add another item to remove this one."
                    overlayInnerStyle={{ maxWidth: 300 }}
                  >
                    {tagElement}
                  </Tooltip>
                );
              }

              return tagElement;
            })}
        </div>

      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        <Table<ItemType>
          dataSource={pagedData}
          key={selectionType}
          rowKey={(record) =>
            selectionType === "project"
              ? (record as ProjectItem).project_id
              : (record as ClinicalStudyItem).study_id
                }
          pagination={false}
          columns={columns}
          />
      </div>

      {/* Footer */}
      <div
        style={{
          paddingTop: 8,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Pagination
          pageSize={pageSize}
          current={currentPage}
          onChange={(page) => setCurrentPage(page)}
          total={filteredData.length}
        />
        <Button type="primary" onClick={applySelection}>
          Apply Selection
        </Button>
      </div>
    </div>
  );
};

export default SelectionPopup;